import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';
import { formatErrorMessage } from '../../utils/errorFormatter';

const TrainingCourses = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [filterType, setFilterType] = useState('All');

  const [courseForm, setCourseForm] = useState({
    course_title: '',
    course_code: '',
    training_type: 'Mandatory',
    description: '',
    trainer_institution: '',
    duration_hours: '',
    validity_period_months: '',
    has_quiz: false,
    passing_score: '',
    evidence_required: false,
    cost: ''
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    if (filterType === 'All') {
      setFilteredCourses(courses);
    } else {
      setFilteredCourses(courses.filter(c => c.training_type === filterType));
    }
  }, [filterType, courses]);

  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/training-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
      setFilteredCourses(response.data);
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to fetch courses'));
    } finally {
      setLoading(false);
    }
  };

  const openModal = (course = null) => {
    if (course) {
      setEditingCourse(course);
      setCourseForm({
        course_title: course.course_title,
        course_code: course.course_code || '',
        training_type: course.training_type,
        description: course.description || '',
        trainer_institution: course.trainer_institution || '',
        duration_hours: course.duration_hours || '',
        validity_period_months: course.validity_period_months || '',
        has_quiz: course.has_quiz || false,
        passing_score: course.passing_score || '',
        evidence_required: course.evidence_required || false,
        cost: course.cost || ''
      });
    } else {
      setEditingCourse(null);
      setCourseForm({
        course_title: '',
        course_code: '',
        training_type: 'Mandatory',
        description: '',
        trainer_institution: '',
        duration_hours: '',
        validity_period_months: '',
        has_quiz: false,
        passing_score: '',
        evidence_required: false,
        cost: ''
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...courseForm,
        duration_hours: courseForm.duration_hours ? parseFloat(courseForm.duration_hours) : null,
        validity_period_months: courseForm.validity_period_months ? parseInt(courseForm.validity_period_months) : null,
        passing_score: courseForm.passing_score ? parseInt(courseForm.passing_score) : null,
        cost: courseForm.cost ? parseFloat(courseForm.cost) : null
      };

      if (editingCourse) {
        await axios.put(
          `${API}/training-courses/${editingCourse.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSuccess('Training course updated successfully');
      } else {
        await axios.post(
          `${API}/training-courses`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        showSuccess('Training course created successfully');
      }

      setShowModal(false);
      fetchCourses();
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to save course'));
    }
  };

  const handleDelete = async (courseId) => {
    const confirmed = await confirm(
      'Delete Training Course',
      'Are you sure you want to delete this training course? This action cannot be undone.',
      { destructive: true, confirmLabel: 'Delete' }
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/training-courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Training course deleted successfully');
      fetchCourses();
    } catch (err) {
      showError(formatErrorMessage(err, 'Failed to delete course'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/training')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Training Dashboard
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📖 Training Courses</h1>
              <p className="text-gray-600 mt-1">{filteredCourses.length} course(s) found</p>
            </div>
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
              <button
                onClick={() => openModal()}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                New Course
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex gap-2">
            {['All', 'Mandatory', 'Job-Specific', 'Refresher', 'Contractor Training'].map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  filterType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map(course => (
            <div key={course.id} className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    course.training_type === 'Mandatory' ? 'bg-red-100 text-red-700' :
                    course.training_type === 'Job-Specific' ? 'bg-blue-100 text-blue-700' :
                    course.training_type === 'Refresher' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {course.training_type}
                  </span>
                </div>
                {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => openModal(course)}
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(course.id)}
                      className="text-red-600 hover:underline text-sm"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>

              <h3 className="font-bold text-lg text-gray-800 mb-2">{course.course_title}</h3>
              {course.course_code && (
                <p className="text-sm text-gray-500 mb-2">Code: {course.course_code}</p>
              )}
              {course.description && (
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{course.description}</p>
              )}

              <div className="space-y-1 text-sm text-gray-600">
                {course.trainer_institution && (
                  <p>🏢 {course.trainer_institution}</p>
                )}
                {course.duration_hours && (
                  <p>⏱️ {course.duration_hours} hours</p>
                )}
                {course.validity_period_months && (
                  <p>📅 Valid for {course.validity_period_months} months</p>
                )}
                {course.has_quiz && (
                  <p>✅ Has Quiz (Pass: {course.passing_score}%)</p>
                )}
                {course.evidence_required && (
                  <p>📄 Evidence Required</p>
                )}
              </div>
            </div>
          ))}
        </div>

        {filteredCourses.length === 0 && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600">No training courses found</p>
            {(user?.role === 'admin' || user?.role === 'Admin' || user?.role === 'hse_manager' || user?.role === 'HSE Manager') && (
              <button
                onClick={() => openModal()}
                className="mt-4 text-blue-600 hover:underline font-semibold"
              >
                Create your first course
              </button>
            )}
          </div>
        )}

        {/* Course Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingCourse ? 'Edit Course' : 'New Training Course'}
                </h2>
                <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Course Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={courseForm.course_title}
                      onChange={(e) => setCourseForm({ ...courseForm, course_title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Course Code</label>
                    <input
                      type="text"
                      value={courseForm.course_code}
                      onChange={(e) => setCourseForm({ ...courseForm, course_code: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Training Type</label>
                    <select
                      value={courseForm.training_type}
                      onChange={(e) => setCourseForm({ ...courseForm, training_type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Mandatory">Mandatory</option>
                      <option value="Job-Specific">Job-Specific</option>
                      <option value="Refresher">Refresher</option>
                      <option value="Contractor Training">Contractor Training</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                    <textarea
                      value={courseForm.description}
                      onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Trainer/Institution</label>
                    <input
                      type="text"
                      value={courseForm.trainer_institution}
                      onChange={(e) => setCourseForm({ ...courseForm, trainer_institution: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (hours)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={courseForm.duration_hours}
                      onChange={(e) => setCourseForm({ ...courseForm, duration_hours: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Validity (months)</label>
                    <input
                      type="number"
                      value={courseForm.validity_period_months}
                      onChange={(e) => setCourseForm({ ...courseForm, validity_period_months: e.target.value })}
                      placeholder="e.g., 12, 36"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Cost</label>
                    <input
                      type="number"
                      step="0.01"
                      value={courseForm.cost}
                      onChange={(e) => setCourseForm({ ...courseForm, cost: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Passing Score (%) {courseForm.has_quiz && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={courseForm.passing_score}
                      onChange={(e) => setCourseForm({ ...courseForm, passing_score: e.target.value })}
                      disabled={!courseForm.has_quiz}
                      className={`w-full px-4 py-2 border rounded-lg disabled:bg-gray-100 ${
                        courseForm.has_quiz && (!courseForm.passing_score || parseInt(courseForm.passing_score) <= 0)
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                    />
                    {courseForm.has_quiz && (!courseForm.passing_score || parseInt(courseForm.passing_score) <= 0) && (
                      <p className="text-xs text-red-500 mt-1">Passing score is required when quiz is enabled</p>
                    )}
                  </div>

                  <div className="col-span-2 space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={courseForm.has_quiz}
                        onChange={(e) => setCourseForm({ ...courseForm, has_quiz: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Has Quiz</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={courseForm.evidence_required}
                        onChange={(e) => setCourseForm({ ...courseForm, evidence_required: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Evidence Required</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!courseForm.course_title || (courseForm.has_quiz && (!courseForm.passing_score || parseInt(courseForm.passing_score) <= 0))}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                >
                  {editingCourse ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default TrainingCourses;
